import PropTypes from 'prop-types';
import { BsEnvelope, BsEnvelopePaper, BsFileText } from 'react-icons/bs';

import Select from '#globalComponents/Forms/Select';
import Textarea from '#globalComponents/Forms/Textarea';
import TextInput from '#globalComponents/Forms/TextInput';

const TemplateSelector = ({
	isEmailChannel,
	emailTemplates,
	postMailingTemplates,
	selectedEmailTemplateId,
	selectedPostMailingTemplateId,
	setSelectedEmailTemplateId,
	setSelectedPostMailingTemplateId,
	eligibleContacts,
	selectedContactId,
	setSelectedContactId,
	resolvedCompanyId,
}) => {
	const templates = isEmailChannel ? emailTemplates : postMailingTemplates;
	const selectedTemplateId = isEmailChannel ? selectedEmailTemplateId : selectedPostMailingTemplateId;
	const setSelectedTemplateId = isEmailChannel ? setSelectedEmailTemplateId : setSelectedPostMailingTemplateId;
	const templatesHref = isEmailChannel ? '/email-campaigns/templates' : '/post-mailing/templates';

	return (
		<div>
			<Select value={selectedTemplateId} onChange={(e) => setSelectedTemplateId(e.target.value)} className="mt-1">
				<option value="">Template wählen…</option>
				{templates.map((tpl) => (
					<option key={tpl.id} value={tpl.id}>
						{tpl.name}
					</option>
				))}
			</Select>
			{templates.length === 0 && (
				<p className="text-[11px] text-bgBase-800 mt-1">
					Noch keine Templates vorhanden.{' '}
					<a href={templatesHref} className="text-primary-600 hover:text-primary-700">
						Jetzt erstellen →
					</a>
				</p>
			)}
			{eligibleContacts.length > 0 ? (
				<Select
					value={selectedContactId}
					onChange={(e) => setSelectedContactId(e.target.value)}
					className="mt-1.5"
				>
					<option value="">Empfänger wählen…</option>
					{eligibleContacts.map((c) => (
						<option key={c.id} value={c.id}>
							{c.fullName ??
								(`${c.firstName ?? ''} ${c.lastName ?? ''}`.trim() || c.emailAddress || 'Unbenannt')}
							{isEmailChannel && c.emailAddress ? ` (${c.emailAddress})` : ''}
						</option>
					))}
				</Select>
			) : resolvedCompanyId ? (
				<p className="text-[11px] text-caution-700 mt-1.5">
					{isEmailChannel
						? 'Kein Kontakt mit E-Mail-Adresse vorhanden.'
						: 'Kein Kontakt vorhanden. Bitte zuerst einen Kontakt anlegen.'}
				</p>
			) : null}
		</div>
	);
};

TemplateSelector.propTypes = {
	isEmailChannel: PropTypes.bool.isRequired,
	emailTemplates: PropTypes.array.isRequired,
	postMailingTemplates: PropTypes.array.isRequired,
	selectedEmailTemplateId: PropTypes.string.isRequired,
	selectedPostMailingTemplateId: PropTypes.string.isRequired,
	setSelectedEmailTemplateId: PropTypes.func.isRequired,
	setSelectedPostMailingTemplateId: PropTypes.func.isRequired,
	eligibleContacts: PropTypes.array.isRequired,
	selectedContactId: PropTypes.string.isRequired,
	setSelectedContactId: PropTypes.func.isRequired,
	resolvedCompanyId: PropTypes.string,
};

const ActivityFieldsByChannel = ({
	mode,
	activeChannels,
	channelTypeId,
	setChannelTypeId,
	isCustomChannel,
	customLabel,
	setCustomLabel,
	isEmailChannel,
	showTemplateOption,
	templateMode,
	setTemplateMode,
	isTemplateMode,
	emailTemplates,
	postMailingTemplates,
	selectedEmailTemplateId,
	setSelectedEmailTemplateId,
	selectedPostMailingTemplateId,
	setSelectedPostMailingTemplateId,
	eligibleContacts,
	selectedContactId,
	setSelectedContactId,
	resolvedCompanyId,
	title,
	setTitle,
	body,
	setBody,
	dueAt,
	setDueAt,
}) => (
	<>
		{/* Channel picker (not for notes) */}
		{mode !== 'note' && (
			<div className="flex gap-2">
				<div className="flex-shrink-0">
					<label className="block text-xs font-semibold text-text uppercase tracking-wide mb-1.5">
						Kanal
					</label>
					<Select value={channelTypeId} onChange={(e) => setChannelTypeId(e.target.value)}>
						{activeChannels.map((c) => (
							<option key={c.id} value={c.id}>
								{c.displayName}
							</option>
						))}
					</Select>
				</div>
				{isCustomChannel && (
					<div className="flex-1">
						<label className="block text-xs font-semibold text-text uppercase tracking-wide mb-1.5">
							Art
						</label>
						<TextInput
							placeholder="z. B. Vor-Ort-Termin"
							value={customLabel}
							onChange={(e) => setCustomLabel(e.target.value)}
						/>
					</div>
				)}
			</div>
		)}

		{/* Template selection for email / letter channels in todo mode */}
		{showTemplateOption && (
			<div className="space-y-2">
				<label className="block text-xs font-semibold text-text uppercase tracking-wide">Versandart</label>
				<div className="flex gap-2">
					<button
						type="button"
						onClick={() => setTemplateMode('template')}
						className={`flex-1 flex items-center gap-2 px-3 py-2.5 rounded-card border text-sm transition-colors ${
							templateMode === 'template'
								? 'border-primary-300 bg-primary-100 text-primary-700 font-medium'
								: 'border-bgBase-500 text-text hover:border-bgBase-600'
						}`}
					>
						<BsFileText className="w-3.5 h-3.5 flex-shrink-0" />
						<div className="text-left">
							<div className="font-medium text-xs">Mit Template versenden</div>
							<div className="text-[10px] text-bgBase-800 mt-0.5">
								Automatischer Versand zum geplanten Zeitpunkt
							</div>
						</div>
					</button>
					<button
						type="button"
						onClick={() => setTemplateMode('manual')}
						className={`flex-1 flex items-center gap-2 px-3 py-2.5 rounded-card border text-sm transition-colors ${
							templateMode === 'manual'
								? 'border-primary-300 bg-primary-100 text-primary-700 font-medium'
								: 'border-bgBase-500 text-text hover:border-bgBase-600'
						}`}
					>
						{isEmailChannel ? (
							<BsEnvelope className="w-3.5 h-3.5 flex-shrink-0" />
						) : (
							<BsEnvelopePaper className="w-3.5 h-3.5 flex-shrink-0" />
						)}
						<div className="text-left">
							<div className="font-medium text-xs">Manuell erledigen</div>
							<div className="text-[10px] text-bgBase-800 mt-0.5">Erinnerung — selbst versenden</div>
						</div>
					</button>
				</div>

				{templateMode === 'template' && (
					<TemplateSelector
						isEmailChannel={isEmailChannel}
						emailTemplates={emailTemplates}
						postMailingTemplates={postMailingTemplates}
						selectedEmailTemplateId={selectedEmailTemplateId}
						setSelectedEmailTemplateId={setSelectedEmailTemplateId}
						selectedPostMailingTemplateId={selectedPostMailingTemplateId}
						setSelectedPostMailingTemplateId={setSelectedPostMailingTemplateId}
						eligibleContacts={eligibleContacts}
						selectedContactId={selectedContactId}
						setSelectedContactId={setSelectedContactId}
						resolvedCompanyId={resolvedCompanyId}
					/>
				)}
			</div>
		)}

		{mode !== 'note' && (
			<div>
				<label className="block text-xs font-semibold text-text uppercase tracking-wide mb-1.5">
					{mode === 'log' ? 'Was wurde gemacht?' : 'Was steht an?'} *
				</label>
				<TextInput
					value={title}
					onChange={(e) => setTitle(e.target.value)}
					placeholder={mode === 'log' ? 'z. B. Angebot besprochen' : 'z. B. Follow-up Anruf'}
					required
				/>
			</div>
		)}

		<div>
			<label className="block text-xs font-semibold text-text uppercase tracking-wide mb-1.5">
				{mode === 'note' ? 'Notiz *' : 'Details'}
			</label>
			<Textarea
				value={body}
				onChange={(e) => setBody(e.target.value)}
				rows={mode === 'note' ? 4 : 2}
				placeholder={mode === 'note' ? 'Notizinhalt …' : 'Optional …'}
				className="!resize-none"
				required={mode === 'note'}
			/>
		</div>

		{/* Due date for todos */}
		{mode === 'todo' && (
			<div>
				<label className="block text-xs font-semibold text-text uppercase tracking-wide mb-1.5">
					Fällig am{isTemplateMode ? ' *' : ''}
				</label>
				<TextInput
					type="datetime-local"
					value={dueAt}
					onChange={(e) => setDueAt(e.target.value)}
					min={(() => {
						const d = new Date();
						d.setSeconds(0, 0);
						return d.toISOString().slice(0, 16);
					})()}
				/>
				{isTemplateMode && (
					<p className="text-[10px] text-bgBase-800 mt-0.5">Zeitpunkt für automatischen Versand</p>
				)}
			</div>
		)}
	</>
);

ActivityFieldsByChannel.propTypes = {
	mode: PropTypes.string.isRequired,
	activeChannels: PropTypes.array.isRequired,
	channelTypeId: PropTypes.string.isRequired,
	setChannelTypeId: PropTypes.func.isRequired,
	isCustomChannel: PropTypes.bool.isRequired,
	customLabel: PropTypes.string.isRequired,
	setCustomLabel: PropTypes.func.isRequired,
	isEmailChannel: PropTypes.bool.isRequired,
	showTemplateOption: PropTypes.bool.isRequired,
	templateMode: PropTypes.string.isRequired,
	setTemplateMode: PropTypes.func.isRequired,
	isTemplateMode: PropTypes.bool.isRequired,
	emailTemplates: PropTypes.array.isRequired,
	postMailingTemplates: PropTypes.array.isRequired,
	selectedEmailTemplateId: PropTypes.string.isRequired,
	setSelectedEmailTemplateId: PropTypes.func.isRequired,
	selectedPostMailingTemplateId: PropTypes.string.isRequired,
	setSelectedPostMailingTemplateId: PropTypes.func.isRequired,
	eligibleContacts: PropTypes.array.isRequired,
	selectedContactId: PropTypes.string.isRequired,
	setSelectedContactId: PropTypes.func.isRequired,
	resolvedCompanyId: PropTypes.string,
	title: PropTypes.string.isRequired,
	setTitle: PropTypes.func.isRequired,
	body: PropTypes.string.isRequired,
	setBody: PropTypes.func.isRequired,
	dueAt: PropTypes.string.isRequired,
	setDueAt: PropTypes.func.isRequired,
};

export default ActivityFieldsByChannel;
